import { AmbientGlow, GlassCard, ScrollReveal } from '@/components/design-system';
import BrowseListingsPanel from '@/components/company/BrowseListingsPanel';
import MyPickupsPanel from '@/components/company/MyPickupsPanel';
import PageShell from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeUsers, type UsersResponse } from '@/lib/collections/users';
import { useAuth } from '@pooflabs/web';
import { Factory, Wallet } from 'lucide-react';

/**
 * Recycling Company dashboard: browse open recovery listings, accept +
 * schedule pickups, then settle payment onchain to complete them. Gated to
 * wallets with role === 'company', mirroring the household /Scan gate.
 */
export function CompanyPage() {
  const { user, login } = useAuth();

  const { data: profile, loading: profileLoading } = useRealtimeData<UsersResponse | null>(
    subscribeUsers,
    !!user?.address,
    user?.address ?? ''
  );

  if (!user?.address) {
    return (
      <PageShell>
        <div className="container flex min-h-[70vh] items-center justify-center">
          <ScrollReveal className="relative w-full max-w-md">
            <AmbientGlow color="amber" size={360} position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <GlassCard padding="lg" className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
                <Wallet className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <span className="text-overline">Company Dashboard</span>
              <h1 className="font-heading mt-2 text-2xl font-bold text-foreground">Connect to get started</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Connect your wallet to browse recovery listings and accept pickups.
              </p>
              <Button
                onClick={() => login()}
                size="lg"
                className="mt-6 w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
              >
                Connect wallet
              </Button>
            </GlassCard>
          </ScrollReveal>
        </div>
      </PageShell>
    );
  }

  if (!profileLoading && profile && profile.role !== 'company') {
    return (
      <PageShell>
        <div className="container flex min-h-[70vh] items-center justify-center">
          <ScrollReveal className="relative w-full max-w-md">
            <AmbientGlow color="primary" size={360} position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <GlassCard padding="lg" className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
                <Factory className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <span className="text-overline">Company Dashboard</span>
              <h1 className="font-heading mt-2 text-2xl font-bold text-foreground">Company-only space</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                This dashboard is for recycling companies accepting pickups. Your profile is set up as{' '}
                <span className="font-medium text-foreground">{profile.role}</span>.
              </p>
            </GlassCard>
          </ScrollReveal>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="container relative py-8">
        <AmbientGlow color="amber" size={420} position="top-0 right-0 translate-x-1/3 -translate-y-1/3" />
        <AmbientGlow color="primary" size={380} position="bottom-0 left-0 -translate-x-1/3 translate-y-1/3" />

        <ScrollReveal className="mb-8">
          <span className="text-overline">Company Dashboard</span>
          <h1 className="font-heading mt-1 text-3xl font-bold text-foreground sm:text-4xl">
            Recover it. Pay for it. Track it.
          </h1>
        </ScrollReveal>

        <ScrollReveal delay={0.05} className="relative">
          <Tabs defaultValue="browse" className="w-full">
            <TabsList className="mb-6 h-11 rounded-full border border-border/60 bg-card/60 p-1 backdrop-blur-xl">
              <TabsTrigger
                value="browse"
                className="rounded-full px-5 text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-accent/20 data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                Open Listings
              </TabsTrigger>
              <TabsTrigger
                value="pickups"
                className="rounded-full px-5 text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/20 data-[state=active]:to-accent/20 data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                My Pickups
              </TabsTrigger>
            </TabsList>

            <TabsContent value="browse" className="mt-0">
              <BrowseListingsPanel userAddress={user.address} />
            </TabsContent>

            <TabsContent value="pickups" className="mt-0">
              <MyPickupsPanel userAddress={user.address} />
            </TabsContent>
          </Tabs>
        </ScrollReveal>
      </div>
    </PageShell>
  );
}

export default CompanyPage;
